import { Icon, Menu, Popover, Position } from "@blueprintjs/core";
import React from "react";
import styled, { css } from "styled-components";

import { IconNames } from "@blueprintjs/icons";
import { IViewProps, View } from "core/decorators/View";
import { isEmpty } from "core/util/ArrayUtils";
import { toBlockCss } from "../common/BlockStylesConfig";
import { toTypographyCss } from "../common/TypographyStylesConfig";
import { DropdownSelectConfig } from "./DropdownSelectConfig";

/**
 * @description view for users to select values from a list of provided options.
 * coming soon: registered with "view" conditions for use in a UI-facing form!
 * @author chills
 */

export enum DropdownSelectVariant {
	MINIMAL = "minimal",
	PRIMARY = "primary",
	SECONDARY = "secondary"
}

interface IItemRendererArgs {
	handleSelect: (e: React.MouseEvent<any>) => void;
}

export type IItemRenderer<T> = (item: T, args: IItemRendererArgs, index: number) => React.ReactNode;

interface IDropdownSelectProps<T> extends IViewProps<DropdownSelectConfig, DropdownSelectVariant> {
	items: T[];
	itemRenderer: IItemRenderer<T>;
	onChange: (item: T, index?: number) => void;
	label?: string | JSX.Element;
	disabled?: boolean;
	readonly?: boolean;
	popOverContentStyles?: any;
}

interface IDropdownSelectState {
	isOpen?: boolean;
}

interface IDropdownButtonProps {
	active?: boolean;
	config: DropdownSelectConfig;
	disabled?: boolean;
}

// changed inline-block to flex to align items
const StyledDropdownButton = styled.a<IDropdownButtonProps>`
	${({ config }: IDropdownButtonProps) => toTypographyCss(config.typography)}
	   color: inherit;
	${({ active, config, disabled }: IDropdownButtonProps) => {
		if (active) {
			return toBlockCss(config.activeState);
		}
		if (disabled) {
			return toBlockCss(config.disabledState);
		}
		return css`
			${toBlockCss(config.defaultState)}
			&:hover {
				${toBlockCss(config.hoverState)}
				color: ${config.hoverState ? config.hoverState.color : "inherit"};
			}
			&:active, &:focus {
				${toBlockCss(config.activeState)}
			}
		`;
	}}

	display: flex;
	align-items: center;
`;

@View({ id: "dropdown-select", configClass: DropdownSelectConfig, variants: DropdownSelectVariant })
export class DropdownSelectView<T = any> extends React.PureComponent<IDropdownSelectProps<T>, IDropdownSelectState> {

	public state = { isOpen: false };

	public render() {
		const { config, disabled, items, readonly, popOverContentStyles } = this.props;

		const btn = (
			<StyledDropdownButton active={this.state.isOpen} config={config} disabled={disabled || readonly}>
				<Icon icon={IconNames.CHEVRON_DOWN} /> {this.props.children}
			</StyledDropdownButton>
		);

		// no popover for you
		if (disabled || isEmpty(items)) {
			return btn;
		}

		return (
			<Popover content={<Menu style={popOverContentStyles} children={items.map(this.renderItem)} />} position={Position.BOTTOM}>
				{btn}
			</Popover>
		);
	}

	private renderItem = (item: T, index: number): React.ReactNode => this.props.itemRenderer(item, { handleSelect: this.handleSelect(item, index) }, index);

	private handleSelect = (value: T, index: number) => () => this.props.onChange(value, index);
}

export function ofType<T>() {
	return DropdownSelectView as new (props: IDropdownSelectProps<T>) => DropdownSelectView<T>;
}



// WEBPACK FOOTER //
// ./src/common/views/dropdownSelect/DropdownSelect.view.tsx